import { useEffect, useState, useCallback } from "react";
import { Settings, Loader2, UserPlus, Trash2, Palette, Building2, Shield, Crown, Share2, Copy, Globe } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

const PRESET_COLORS = ["#4da3ff", "#30D158", "#FF9F0A", "#BF5AF2", "#FF375F", "#64D2FF", "#FFD60A", "#5E5CE6"];
const ROLE_ICON = { owner: Crown, admin: Shield, member: UserPlus };

export default function WorkspaceSettings() {
  const { active, activeId, refresh } = useWorkspace();
  const [name, setName] = useState("");
  const [color, setColor] = useState("#4da3ff");
  const [logo, setLogo] = useState("");
  const [hipaaMode, setHipaaMode] = useState("auto");
  const [saving, setSaving] = useState(false);

  const [members, setMembers] = useState([]);
  const [myRole, setMyRole] = useState("member");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("member");
  const [inviting, setInviting] = useState(false);

  const [shareToken, setShareToken] = useState(null);
  const [shareBusy, setShareBusy] = useState(false);

  useEffect(() => {
    if (active) {
      setName(active.name || "");
      setColor(active.accent_color || "#4da3ff");
      setLogo(active.logo_url || "");
      setHipaaMode(active.hipaa_mode || "auto");
    }
  }, [active]);

  const loadMembers = useCallback(async () => {
    if (!activeId) return;
    try {
      const res = await api.get(`/workspaces/${activeId}/members`);
      setMembers(res.data.members || []);
      setMyRole(res.data.myRole || "member");
    } catch {}
  }, [activeId]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  const loadShare = useCallback(async () => {
    if (!activeId) return;
    try { const res = await api.get(`/workspaces/${activeId}/share`); setShareToken(res.data.share_token); }
    catch {}
  }, [activeId]);
  useEffect(() => { loadShare(); }, [loadShare]);

  const shareUrl = shareToken ? `${window.location.origin}/share/${shareToken}` : "";
  const toggleShare = async () => {
    setShareBusy(true);
    try {
      if (shareToken) { await api.delete(`/workspaces/${activeId}/share`); setShareToken(null); toast.success("Public link disabled"); }
      else { const res = await api.post(`/workspaces/${activeId}/share`); setShareToken(res.data.share_token); toast.success("Public link enabled"); }
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setShareBusy(false); }
  };
  const copyShare = () => { navigator.clipboard.writeText(shareUrl); toast.success("Link copied"); };

  const canManage = myRole === "owner" || myRole === "admin";

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/workspaces/${activeId}`, { name, accent_color: color, logo_url: logo, hipaa_mode: hipaaMode });
      toast.success("Workspace updated");
      await refresh();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setSaving(false); }
  };

  const invite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const res = await api.post(`/workspaces/${activeId}/members`, { email: inviteEmail.trim(), role: inviteRole });
      toast.success(res.data.note || "Member invited");
      setInviteEmail("");
      await loadMembers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Invite failed"); }
    finally { setInviting(false); }
  };

  const removeMember = async (mid) => {
    try {
      await api.delete(`/workspaces/${activeId}/members/${mid}`);
      toast.success("Member removed");
      await loadMembers();
    } catch (e) { toast.error(e?.response?.data?.detail || "Remove failed"); }
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-1">
          <Settings size={18} className="text-[color:var(--ws-accent,#4da3ff)]" />
          <p className="text-[color:var(--ws-accent,#4da3ff)] text-xs font-semibold uppercase tracking-[0.2em]">Workspace</p>
        </div>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-white" data-testid="ws-settings-title">Settings</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Branding */}
        <div className="bg-[#121212] border border-white/10 rounded-xl p-5" data-testid="branding-card">
          <div className="flex items-center gap-2 mb-4">
            <Palette size={16} className="text-[color:var(--ws-accent,#4da3ff)]" />
            <h2 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">Branding</h2>
          </div>
          <label className="block text-zinc-400 text-sm mb-1.5">Workspace name</label>
          <input data-testid="ws-name-input" value={name} onChange={(e) => setName(e.target.value)} disabled={!canManage}
            className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white mb-4 focus:outline-none focus:border-[color:var(--ws-accent,#4da3ff)]/50 disabled:opacity-60" />

          <label className="block text-zinc-400 text-sm mb-1.5">Logo URL (optional)</label>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center overflow-hidden shrink-0">
              {logo ? <img src={logo} alt="logo" className="w-full h-full object-contain" /> : <Building2 size={18} className="text-zinc-500" />}
            </div>
            <input data-testid="ws-logo-input" value={logo} onChange={(e) => setLogo(e.target.value)} disabled={!canManage} placeholder="https://…/logo.png"
              className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[color:var(--ws-accent,#4da3ff)]/50 disabled:opacity-60" />
          </div>

          <label className="block text-zinc-400 text-sm mb-2">Accent color</label>
          <div className="flex items-center gap-2 flex-wrap mb-5">
            {PRESET_COLORS.map((c) => (
              <button key={c} data-testid={`color-${c}`} onClick={() => canManage && setColor(c)}
                className={`w-8 h-8 rounded-full transition-transform ${color === c ? "ring-2 ring-offset-2 ring-offset-[#121212] scale-110" : ""}`}
                style={{ background: c, boxShadow: color === c ? `0 0 0 2px ${c}` : "none" }} />
            ))}
          </div>

          <label className="block text-zinc-400 text-sm mb-2">Healthcare / HIPAA checks</label>
          <div className="grid grid-cols-3 gap-2 mb-5" data-testid="hipaa-mode-toggle">
            {[
              { v: "auto", label: "Auto", hint: "Only for healthcare sites" },
              { v: "on", label: "Always on", hint: "Show on every site" },
              { v: "off", label: "Off", hint: "Never show" },
            ].map((o) => (
              <button
                key={o.v}
                data-testid={`hipaa-mode-${o.v}`}
                onClick={() => canManage && setHipaaMode(o.v)}
                disabled={!canManage}
                className={`text-left rounded-lg border px-3 py-2 transition-colors ${hipaaMode === o.v ? "border-[color:var(--ws-accent,#4da3ff)] bg-[color:var(--ws-accent,#4da3ff)]/10" : "border-white/10 hover:bg-white/5"}`}
              >
                <span className="block text-white text-sm font-medium">{o.label}</span>
                <span className="block text-zinc-500 text-[11px] leading-tight mt-0.5">{o.hint}</span>
              </button>
            ))}
          </div>

          <button data-testid="ws-save-btn" onClick={save} disabled={!canManage || saving}
            className="btn-brand text-white rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 size={15} className="animate-spin" /> : null} Save changes
          </button>
          {!canManage && <p className="text-zinc-500 text-xs mt-3">Only owners and admins can edit branding.</p>}
        </div>

        {/* Members */}
        <div className="bg-[#121212] border border-white/10 rounded-xl p-5" data-testid="members-card">
          <div className="flex items-center gap-2 mb-4">
            <UserPlus size={16} className="text-[color:var(--ws-accent,#4da3ff)]" />
            <h2 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">Team members</h2>
          </div>

          {canManage && (
            <div className="flex flex-col sm:flex-row gap-2 mb-4">
              <input data-testid="invite-email-input" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="teammate@company.com"
                className="flex-1 bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[color:var(--ws-accent,#4da3ff)]/50" />
              <select data-testid="invite-role-select" value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
                className="bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-sm text-white focus:outline-none">
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
              <button data-testid="invite-btn" onClick={invite} disabled={inviting || !inviteEmail.trim()}
                className="btn-brand text-white rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60">
                {inviting ? <Loader2 size={15} className="animate-spin" /> : <UserPlus size={15} />} Invite
              </button>
            </div>
          )}

          <div className="space-y-2">
            {members.map((m) => {
              const RIcon = ROLE_ICON[m.role] || UserPlus;
              return (
                <div key={m.membership_id} data-testid={`member-${m.membership_id}`} className="flex items-center gap-3 border border-white/10 rounded-lg p-3">
                  <div className="w-9 h-9 rounded-full bg-white/5 flex items-center justify-center overflow-hidden shrink-0">
                    {m.picture ? <img src={m.picture} alt="" className="w-full h-full object-cover" /> : <span className="text-zinc-400 text-sm font-semibold">{(m.email || "?")[0].toUpperCase()}</span>}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm truncate">{m.name || m.email}</p>
                    <p className="text-zinc-500 text-xs truncate">{m.email}{m.status === "invited" ? " · invite pending" : ""}</p>
                  </div>
                  <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-1 rounded-full text-zinc-300 bg-white/5 capitalize"><RIcon size={11} /> {m.role}</span>
                  {canManage && m.role !== "owner" && (
                    <button data-testid={`remove-member-${m.membership_id}`} onClick={() => removeMember(m.membership_id)} className="text-zinc-500 hover:text-[#FF3B30] transition-colors">
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Client-facing share link */}
      <div className="bg-[#121212] border border-white/10 rounded-xl p-5 mt-5" data-testid="share-card">
        <div className="flex items-center gap-2 mb-2">
          <Share2 size={16} className="text-[color:var(--ws-accent,#4da3ff)]" />
          <h2 className="font-heading font-semibold text-white text-sm uppercase tracking-wide">Client share link</h2>
        </div>
        <p className="text-zinc-400 text-sm mb-4 max-w-2xl">Publish a read-only, branded status dashboard your clients can view without an account. Great for agencies — show clients what you're monitoring for them.</p>
        {shareToken ? (
          <div className="flex flex-col sm:flex-row items-stretch gap-2">
            <div className="flex-1 flex items-center gap-2 bg-black/40 border border-white/10 rounded-lg px-3 py-2 min-w-0">
              <Globe size={14} className="text-zinc-500 shrink-0" />
              <span data-testid="share-url" className="text-zinc-300 text-sm font-mono truncate">{shareUrl}</span>
            </div>
            <button data-testid="copy-share-btn" onClick={copyShare} className="border border-white/15 text-white hover:bg-white/10 rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2 transition-colors">
              <Copy size={14} /> Copy
            </button>
            <a data-testid="open-share-link" href={shareUrl} target="_blank" rel="noreferrer" className="border border-white/15 text-white hover:bg-white/10 rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2 transition-colors">Preview</a>
            <button data-testid="disable-share-btn" onClick={toggleShare} disabled={shareBusy || !canManage} className="text-[#FF6961] border border-[#FF3B30]/30 hover:bg-[#FF3B30]/10 rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-60">
              {shareBusy ? <Loader2 size={14} className="animate-spin" /> : "Disable"}
            </button>
          </div>
        ) : (
          <button data-testid="enable-share-btn" onClick={toggleShare} disabled={shareBusy || !canManage} className="btn-brand text-white rounded-lg px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60">
            {shareBusy ? <Loader2 size={15} className="animate-spin" /> : <Share2 size={15} />} Enable public link
          </button>
        )}
        {!canManage && <p className="text-zinc-500 text-xs mt-3">Only owners and admins can manage sharing.</p>}
      </div>
    </Layout>
  );
}
